import { useEffect } from "react";
import { MapPin, Clock, Users, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { siteConfig } from "@/config/siteConfig";

export default function LongDistanceTravel() {
  useEffect(() => {
    window.scrollTo(0, 0);
    console.log("bookingEnabled:", siteConfig.bookingEnabled);
  }, []);
  const handleBook = () => {
    window.open(siteConfig.bookingUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="relative py-20 bg-gradient-to-b from-primary/10 to-background">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6">
            <span>← Back to Home</span>
          </Link>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            Long Distance <span className="text-gradient">Travel</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl">
            Experience comfortable, reliable cross-border journeys with multiple stops and premium service.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          {/* Left Column */}
          <div>
            <h2 className="font-display text-3xl font-bold mb-6">Why Choose CityLink for Long Distance Travel?</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Precise Scheduling</h3>
                  <p className="text-muted-foreground">
                    Our coaches depart on time, every time. We understand the value of your schedule and respect it.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Maximum Comfort</h3>
                  <p className="text-muted-foreground">
                    Spacious reclining seats, climate control, USB charging, and entertainment systems for a restful journey.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Strategic Stops</h3>
                  <p className="text-muted-foreground">
                    Multiple pickup and drop-off points in Johannesburg and Maputo for your convenience.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Professional Service</h3>
                  <p className="text-muted-foreground">
                    Experienced drivers trained in cross-border protocols with 24/7 customer support.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="bg-card rounded-2xl p-8 border border-border/50">
            <h2 className="font-display text-2xl font-bold mb-6">Our Routes</h2>
            <div className="space-y-6">
              {siteConfig.routes.map((route) => (
                <div key={route.id} className="pb-6 border-b border-border/50 last:border-0">
                  <h3 className="font-semibold text-lg mb-3">
                    {route.from} <span className="text-muted-foreground">to</span> {route.to}
                  </h3>
                  <div className="space-y-2 text-sm text-muted-foreground mb-4">
                    <div>Departure: {route.departure}</div>
                    <div>Arrival: {route.arrival}</div>
                  </div>
                  {siteConfig.bookingEnabled ? (
                    <Button onClick={handleBook} className="w-full">
                      Book This Route
                    </Button>
                  ) : (
                    <Button disabled className="w-full bg-muted text-muted-foreground cursor-not-allowed opacity-60 pointer-events-none">
                      Booking Coming Soon
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-12 mb-16">
          <h2 className="font-display text-3xl font-bold mb-8 text-center">Amenities Included</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {siteConfig.amenities.map((amenity, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-primary" />
                <span className="font-medium">{amenity}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center py-12">
          <h2 className="font-display text-3xl font-bold mb-4">Ready to Travel?</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Book your long-distance journey today and experience premium cross-border travel.
          </p>
          {siteConfig.bookingEnabled ? (
            <Button onClick={handleBook} size="lg" className="text-lg px-8">
              Book Now
            </Button>
          ) : (
            <Button disabled size="lg" className="text-lg px-8 bg-muted text-muted-foreground cursor-not-allowed opacity-60 pointer-events-none">
              Booking Coming Soon
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
